/**
 * 用户管理 Tab
 * 自带：筛选（用户名 / 充值大于 / 项目数大于）+ 分页 + 15s 在线状态轮询。
 */
import React, { useState, useEffect, useCallback } from 'react';
import { fmtDateTimeCST } from '../utils/datetime';
import { UserPlusIcon, PencilSquareIcon, TrashIcon, EyeIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { UserInfo } from '../types';
import { ROLE_LABELS } from './RolesTab';
import { authApi } from '../services/api';

interface UsersTabProps {
  refreshKey: number;
  openCreateForm: () => void;
  openEditForm: (u: UserInfo) => void;
  openViewUser: (u: UserInfo) => void;
  handleToggleActive: (u: UserInfo) => void;
  handleDeleteUser: (u: UserInfo) => void;
}

function UsersTab({
  refreshKey, openCreateForm, openEditForm, openViewUser, handleToggleActive, handleDeleteUser,
}: UsersTabProps) {
  const fmtOnline = (sec: number) => {
    if (sec < 60) return `${sec} 秒`;
    const m = Math.floor(sec / 60);
    if (m < 60) return `${m} 分钟`;
    const h = Math.floor(m / 60);
    return `${h} 小时 ${m % 60} 分`;
  };

  // 筛选表单（未应用输入）与已应用筛选
  const [form, setForm] = useState({ username: '', minRecharge: '', minProjects: '' });
  const [filters, setFilters] = useState({ username: '', minRecharge: '', minProjects: '' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [onlineCount, setOnlineCount] = useState(0);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, unknown> = { page, page_size: pageSize };
      if (filters.username.trim()) params.username = filters.username.trim();
      const mr = parseFloat(filters.minRecharge);
      if (!isNaN(mr)) params.min_recharge = mr;
      const mp = parseInt(filters.minProjects, 10);
      if (!isNaN(mp)) params.min_projects = mp;
      const res = await authApi.listUsers(params);
      setUsers(res.data.users);
      setOnlineCount(res.data.online_count ?? 0);
      setTotal(res.data.total ?? 0);
      setTotalPages(res.data.total_pages ?? 1);
    } catch { /* 静默 */ } finally { setLoading(false); }
  }, [page, pageSize, filters]);

  // 筛选 / 翻页 / 外部刷新触发重新拉取
  useEffect(() => { load(); }, [refreshKey, page, filters, load]);
  // 在线状态轮询
  useEffect(() => {
    const t = window.setInterval(() => { load(); }, 15000);
    return () => window.clearInterval(t);
  }, [load]);

  const applyFilters = () => { setPage(1); setFilters(form); };
  const resetFilters = () => {
    const empty = { username: '', minRecharge: '', minProjects: '' };
    setForm(empty);
    setFilters(empty);
    setPage(1);
  };

  return (
    <section>
      {/* 筛选栏 */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">用户名 / 昵称</label>
          <input
            value={form.username}
            onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
            onKeyDown={(e) => { if (e.key === 'Enter') applyFilters(); }}
            placeholder="模糊搜索"
            className="w-44 px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">充值 &gt; (元)</label>
          <input
            type="number"
            min={0}
            value={form.minRecharge}
            onChange={(e) => setForm((f) => ({ ...f, minRecharge: e.target.value }))}
            onKeyDown={(e) => { if (e.key === 'Enter') applyFilters(); }}
            placeholder="大于"
            className="w-32 px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">项目数 &gt;</label>
          <input
            type="number"
            min={0}
            value={form.minProjects}
            onChange={(e) => setForm((f) => ({ ...f, minProjects: e.target.value }))}
            onKeyDown={(e) => { if (e.key === 'Enter') applyFilters(); }}
            placeholder="大于"
            className="w-32 px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
          />
        </div>
        <button
          onClick={applyFilters}
          className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors"
        >
          <MagnifyingGlassIcon className="w-4 h-4 mr-1" />
          查询
        </button>
        <button
          onClick={resetFilters}
          className="px-3 py-1.5 text-sm rounded-md text-gray-600 bg-white border border-gray-300 hover:bg-gray-100 transition-colors"
        >
          重置
        </button>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-gray-500">每页</span>
          <select
            value={pageSize}
            onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
            className="text-sm border border-gray-300 rounded px-1.5 py-1 w-16"
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span className="text-xs text-green-700">当前在线 <b>{onlineCount}</b> 人</span>
        </div>
      </div>

      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="flex items-baseline gap-3">
            <h3 className="text-sm font-semibold text-gray-900">用户列表</h3>
            <span className="text-xs text-gray-500">用户总数：<b className="text-gray-900">{total}</b></span>
          </div>
          <p className="text-xs text-gray-500 mt-0.5">管理系统中的所有用户账号</p>
        </div>
        <button
          onClick={openCreateForm}
          className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors"
        >
          <UserPlusIcon className="w-4 h-4 mr-1" />
          新增用户
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-sm text-gray-400">加载中...</div>
      ) : (
        <div className="space-y-2">
          {users.map((u) => {
            const roleColor: Record<string, string> = {
              admin: 'bg-red-100 text-red-700',
              enterprise: 'bg-blue-100 text-blue-700',
              agent: 'bg-purple-100 text-purple-700',
              normal: 'bg-gray-200 text-gray-600',
            };
            return (
              <div key={u.id} className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900 truncate">{u.display_name || u.username}</span>
                    <span className="text-xs text-gray-400">@{u.username}</span>
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${roleColor[u.role]}`}>
                      {ROLE_LABELS[u.role]}
                    </span>
                    {u.is_online ? (
                      <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-50 px-1.5 py-0.5 rounded">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500" />在线
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                        <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />离线
                      </span>
                    )}
                    {!u.is_active && <span className="text-xs text-red-500 bg-red-50 px-1.5 py-0.5 rounded">已禁用</span>}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                    <span className="text-gray-400">{fmtDateTimeCST(u.created_at)}</span>
                    {(() => {
                      const totalSec = (u.total_online_seconds ?? 0) + (u.is_online ? (u.online_seconds ?? 0) : 0);
                      return (
                        <span>在线时长: <span className={u.is_online ? 'text-green-600' : 'text-gray-400'}>{fmtOnline(totalSec)}</span></span>
                      );
                    })()}
                    <span>项目数: {u.project_count ?? 0}</span>
                    <span>充值: ¥{(u.recharge_amount ?? 0).toFixed(2)}</span>
                    <span>最近登录: {fmtDateTimeCST(u.last_login_at)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1 ml-4">
                  <button onClick={() => openViewUser(u)} className="p-1.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors" title="查看">
                    <EyeIcon className="w-4 h-4" />
                  </button>
                  <button onClick={() => openEditForm(u)} className="p-1.5 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors" title="编辑">
                    <PencilSquareIcon className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleToggleActive(u)} className={`p-1.5 rounded text-xs transition-colors ${u.is_active ? 'text-gray-400 hover:text-yellow-600 hover:bg-yellow-50' : 'text-green-600 hover:bg-green-50'}`} title={u.is_active ? '禁用' : '启用'}>
                    {u.is_active ? '禁用' : '启用'}
                  </button>
                  <button onClick={() => handleDeleteUser(u)} className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors" title="删除">
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
          {users.length === 0 && (
            <div className="text-center py-12 text-sm text-gray-400">没有符合条件的用户</div>
          )}
        </div>
      )}

      {/* 分页 */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <span className="text-xs text-gray-500">共 {total} 条 · 第 {page}/{totalPages} 页</span>
          <div className="flex items-center gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-600 bg-white hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              上一页
            </button>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-600 bg-white hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              下一页
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

export default UsersTab;
